/**
 * Данные карточки хозяйства — react-query поверх `/farms/{id}/…` (AK-746).
 *
 * Инвалидация после любого массового действия — ОДНА и по префиксу карточки:
 * сводка, список и отделения после переноса/архивации меняются вместе, и
 * обновлять их порознь значит показать человеку экран, где шапка спорит с
 * таблицей.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { farmPrepKeys } from '@/pages/farm/preparation';
import type { FarmFieldsPage, FarmFieldsQuery, FarmSummary } from './types';

/**
 * Размер страницы списка.
 *
 * Раньше здесь стояло 500 «чтобы влезло всё» — и на хозяйстве в 1 200 полей
 * список молча обрывался на пятисотом, без единого намёка, что дальше что-то
 * есть. Страница + «Показать ещё» честнее: видно и сколько показано, и
 * сколько всего.
 */
export const FIELDS_PAGE = 100;

export const farmCardKeys = {
  /** Префикс ВСЕЙ карточки — им и инвалидируем. */
  all: (farmId: string) => ['farm-card', farmId] as const,
  summary: (farmId: string) => ['farm-card', farmId, 'summary'] as const,
  fields: (farmId: string, query: FarmFieldsQuery) =>
    ['farm-card', farmId, 'fields', query] as const,
};

export function useFarmSummary(farmId: string) {
  return useQuery({
    queryKey: farmCardKeys.summary(farmId),
    queryFn: () => api.get<FarmSummary>(`/api/v1/farms/${farmId}/summary`),
    staleTime: 30_000,
  });
}

/**
 * Собирает параметры списка из режима под-вкладки.
 *
 * «Кандидаты сверки» — это НЕ третий статус поля, а активные поля под
 * фильтром сигнала. Отдельный режим на экране, общий фильтр на бэкенде:
 * иначе счётчик сигнала и его список считались бы разными условиями.
 */
export function fieldsParams(query: FarmFieldsQuery, offset = 0): Record<string, string> {
  const params: Record<string, string> = {
    status: query.mode === 'archived' ? 'archived' : 'active',
    limit: String(FIELDS_PAGE),
    offset: String(offset),
  };
  if (query.mode === 'reconcile') params.issue = 'reconcile';
  else if (query.issue) params.issue = query.issue;
  if (query.brigade) params.brigade = query.brigade;
  if (query.search?.trim()) params.search = query.search.trim();
  return params;
}

/**
 * Список полей карточки страницами.
 *
 * `getNextPageParam` считает следующий сдвиг по УЖЕ полученным строкам, а не
 * по номеру страницы: на сервере строки могли измениться между запросами
 * (кто-то архивировал соседнее поле), и «страница N + 1» уехала бы мимо.
 * Сортировка на бэкенде — `name, id`, то есть устойчивая: без второго ключа
 * строки с одинаковым именем могли бы перепрыгивать между страницами, а по
 * этому списку жмут ПАКЕТНЫЕ действия — уехавшая строка попала бы не в тот
 * пакет.
 */
export function useFarmFields(farmId: string, query: FarmFieldsQuery) {
  return useInfiniteQuery({
    queryKey: farmCardKeys.fields(farmId, query),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.get<FarmFieldsPage>(
        `/api/v1/farms/${farmId}/fields?${new URLSearchParams(fieldsParams(query, pageParam as number))}`,
      ),
    getNextPageParam: (_last, pages) => {
      const loaded = pages.reduce((sum, p) => sum + p.items.length, 0);
      const total = pages[0]?.total ?? 0;
      return loaded < total ? loaded : undefined;
    },
    staleTime: 15_000,
  });
}

/**
 * Обёртка любого массового действия карточки.
 *
 * Инвалидация — в `onSettled`, а не в `onSuccess`: при частичном отказе
 * сервер уже изменился, и молчащий экран поверх реальной записи — худшее из
 * состояний (канон «Массовые действия», п. 2).
 */
export function useFarmCardBulk<TArgs>(farmId: string, run: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: farmCardKeys.all(farmId) });
      // Шаг «Поля» полосы готовности считает своя ручка: архивация/возврат
      // меняют его счётчик, и полоса обязана показать это без перезагрузки.
      void qc.invalidateQueries({ queryKey: farmPrepKeys.farm(farmId) });
    },
  });
}

/** Заведение и правка отделения — та же инвалидация карточки. */
export function useSaveBrigade(farmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      brigadeId,
      payload,
    }: {
      brigadeId: string | null;
      payload: { name: string; leader_name: string | null; leader_phone: string | null };
    }) =>
      brigadeId
        ? api.patch(`/api/v1/brigades/${brigadeId}`, payload)
        : api.post('/api/v1/brigades', { ...payload, farm_id: farmId }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: farmCardKeys.all(farmId) });
    },
  });
}
